import { useState, useEffect } from 'react';
import { Card, Toast, ToastContainer } from 'react-bootstrap';
import {
    faPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudDownloadAlt } from '@fortawesome/free-solid-svg-icons';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';
import apiClient from 'helpers/apiService';
import Button from 'components/base/Button';
import CredentialsReveal from './components/CredentialsReveal';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
// Define a tuple type
type TextTuple = [type: 'title1' | 'title2' | 'title3' | 'paragraph', text: string];

// Define an array of tuples
type TextTupleArray = TextTuple[];

interface Assessment {
    assessment_received_at: string;
    assessment_content: TextTupleArray;
    resource_url: string;
    resource_name: string;
    chart_values: any;
}


const HoganCareer = () => {
    const { t } = useTranslation();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },

        {
            label: t('Skills'),
            url: '/myskills'
        },
        {
            label: t('HoganCareer.page_title'),
            active: true
        }
    ];

    const [loading, setLoading] = useState(true);
    const [hoganAssessmentAvailable, setHoganAssessmentAvailable] = useState(false);
    const [hoganUser, setHoganUser] = useState('');
    const [hoganPwd, setHoganPwd] = useState('');


    const [assessment, setAssessment] = useState<Assessment>();

    // State to control Toast messages
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');


    const fetchData = async () => {
        try {
            const { data } = await apiClient.get('/v1/hogan/career');
            setHoganAssessmentAvailable(data.hogan_assessment_available);
            setHoganUser(data.hogan_user_id);
            setHoganPwd(data.hogan_pwd);

            setAssessment({
                ...data.assessment, // Keep all other assessment properties
                chart_values: Object.entries(data.assessment.chart_values).map(([key, value]) => ({
                    name: key, // Preserve key as name
                    value,     // Assign numeric value
                }))
            });


        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStartHogan = async () => {
        window.open("https://www.assessmentlink.com/coreparticipant/participant/login.aspx", '_blank', 'noopener,noreferrer');
    };

    useEffect(() => {
        fetchData();
    }, []);



    if (loading) {
        return <div>Loading...</div>;
    }



    return (
        <div>

            <PageBreadcrumb items={breadcrumbItems} />

            <div>
                <ToastContainer className="p-3" position='top-center'>
                    <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide>
                        <Toast.Body className=''>{toastMessage}</Toast.Body>
                    </Toast>
                </ToastContainer>
            </div>


            {hoganAssessmentAvailable && <Card className='mt-3  mb-3 border border-light rounded-5' >
                <Card.Body>
                    <div className="d-flex p-2 bg-200 mb-2">
                        <Button
                            variant="primary"
                            className="me-4"
                            startIcon={
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                            }
                            onClick={handleStartHogan}
                        >
                            {t('HoganCompass.new_hogan_assessment')}
                        </Button>

                        <CredentialsReveal username={hoganUser} password={hoganPwd} />
                    </div>
                </Card.Body>
            </Card>
            }


            {assessment && (
                <>
                    <div className="d-flex bg-body-secondary" style={{ 'textAlign': 'end' }}>
                        <div className="p-2 flex-grow-1 bg-body-secondary border-bottom">
                            <a href={`${assessment.resource_url}`} target="_blank" rel="noopener noreferrer">
                                <FontAwesomeIcon icon={faCloudDownloadAlt} className="text-primary fs-8 me-1" />
                                Last assessment
                            </a>
                        </div>
                    </div>

                    <Card className='mt-3 border border-light rounded-5' >
                        <Card.Body>
                            <div className="w-100">
                                <ResponsiveContainer width="100%" height={450}>
                                    <BarChart
                                        layout="vertical"
                                        data={assessment.chart_values}
                                        margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis dataKey="name" type="category" width={80} />
                                        <Tooltip />
                                        <Bar dataKey="value" fill="#004aad" barSize={30} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {assessment?.assessment_content.map(([type, text], index) => (
                                <div key={index} className={type === 'title1' || type === 'title2' || type === 'title3' ? 'mt-4' : 'mb-2'}>
                                    {type === 'title1' &&
                                        <h3 className="text-secondary" >{text}</h3>
                                    }

                                    {type === 'title2' &&
                                        <h3 className="text-secondary">{text}</h3>
                                    }

                                    {type === 'title3' &&
                                        <h4 className="text-secondary">{text}</h4>
                                    }

                                    {type === 'paragraph' &&
                                        <p className="text-secondary">{text}</p>
                                    }
                                </div>
                            ))}

                        </Card.Body>
                    </Card>
                </>
            )}

            {!assessment && (
                <Card className='mt-3  mb-3 border border-light rounded-5' >
                    <Card.Body>
                        HOGAN assessment not available
                    </Card.Body>
                </Card>
            )}

        </div >
    );


};

export default HoganCareer;
